/**
 * In-App Browser Escape Utilities
 * Handles escaping from in-app browsers (Facebook, Instagram, etc.) to the default browser
 */

/**
 * Escape to the user's default browser on Android using a generic Intent URL.
 * Uses ACTION_VIEW so Android routes to whatever browser is set as default
 * (Chrome, Samsung Internet, Firefox, etc.)
 *
 * @param {string} url - The full URL to open in the default browser
 * @returns {boolean} Whether the escape was attempted
 */
export const escapeToDefaultBrowser = (url) => {
  try {
    const parsed = new URL(url);
    const base =
      `intent://${parsed.host}${parsed.pathname}${parsed.search}${parsed.hash}` +
      `#Intent;scheme=${parsed.protocol.replace(':', '')};` +
      'action=android.intent.action.VIEW;' +
      'category=android.intent.category.BROWSABLE;';

    // Try Chrome first, fall back to default browser if Chrome is not installed
    const fallbackUrl = url;
    const intentUrl =
      `${base}package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(fallbackUrl)};end`;

    console.log('🔗 Escaping to Chrome via Intent URL', intentUrl);
    window.location.href = intentUrl;
    return true;
  } catch (error) {
    console.error('❌ Failed to escape to default browser:', error);
    return false;
  }
};

/**
 * Escape to Safari on iOS by opening the URL in a new window.
 * In-app browsers typically honour window.open by showing an "Open in Safari?" prompt.
 *
 * @param {string} url - The full URL to open in Safari
 * @returns {boolean} Whether the escape was attempted (false if popup was blocked)
 */
export const escapeToSafari = (url) => {
  try {
    console.log('🔗 Attempting to open in Safari via window.open');
    const opened = window.open(url, '_blank');
    if (opened) {
      console.log('✅ window.open succeeded');
      return true;
    }
    console.warn('⚠️ window.open returned null (popup likely blocked)');
    return false;
  } catch (error) {
    console.error('❌ Failed to escape to Safari:', error);
    return false;
  }
};

/**
 * Fallback: copy the current page URL to the clipboard so the user can paste it
 * into their browser manually.
 *
 * @returns {Promise<boolean>} Whether the copy succeeded
 */
export const copyURLToClipboard = async () => {
  const url = window.location.href;

  // Method 1: Modern Clipboard API
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(url);
      console.log('✅ URL copied to clipboard via Clipboard API');
      return true;
    } catch (error) {
      console.warn('❌ Clipboard API failed:', error);
    }
  }

  // Method 2: execCommand fallback
  try {
    const textArea = document.createElement('textarea');
    textArea.value = url;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textArea);
    if (success) {
      console.log('✅ URL copied to clipboard via execCommand');
      return true;
    }
  } catch (error) {
    console.warn('❌ execCommand fallback failed:', error);
  }

  console.error('💥 All clipboard methods failed');
  return false;
};

/**
 * Build an Intent URL that targets the Nivo app package directly.
 * If the app is installed, Android opens it with the deep link.
 * If not installed, falls back to the provided Play Store URL.
 *
 * @param {string} url - The full deep link URL (e.g. https://links.nivohub.com/dt?i=...&b=...)
 * @param {string} fallbackUrl - The Play Store URL (with referrer) to fall back to
 * @returns {string} The intent:// URL
 */
export const buildAppIntentUrl = (url, fallbackUrl) => {
  const parsed = new URL(url);
  return (
    `intent://${parsed.host}${parsed.pathname}${parsed.search}${parsed.hash}` +
    `#Intent;scheme=${parsed.protocol.replace(':', '')};` +
    'package=com.nivohub.nivo;' +
    `S.browser_fallback_url=${encodeURIComponent(fallbackUrl)};end`
  );
};

/**
 * Construct a Play Store URL with referrer parameters for deferred deep linking.
 *
 * @param {string} i - The invitation UUID
 * @param {string} b - The business UUID
 * @param {string} playStoreURL - The base Play Store URL from Redux (contains package id)
 * @returns {string} Play Store URL with encoded referrer
 */
export const constructPlayStoreWithReferrer = (i, b, playStoreURL) => {
  const packageMatch = playStoreURL.match(/[?&]id=([^&]+)/);
  const packageName = packageMatch ? packageMatch[1] : 'com.nivohub.nivo';
  const referrer = encodeURIComponent(`i=${encodeURIComponent(i)}&b=${encodeURIComponent(b)}`);
  return `https://play.google.com/store/apps/details?id=${packageName}&referrer=${referrer}`;
};
